/**
 * A tiny TTL cache for the "vocabulary" requests an admin list page fires on
 * mount — tag lists, the team directory, saved segments, attribute keys.
 *
 * WHY: those lists barely change, but a list page refetches all of them every
 * time it mounts, and it mounts every time you come back from a detail view.
 * Contacts alone cost six requests per mount and refired all six on the way
 * back. Caching them takes a warm return down to the one request that actually
 * depends on the query.
 *
 * Deliberately NOT a data-fetching library. No loading flags, no retries, no
 * background revalidation, no subscriptions — the call sites never used those.
 * It is a Map with an expiry, and the two properties that make it safe:
 *
 *   - It caches the PROMISE, not the value, so two components mounting together
 *     share one in-flight request instead of racing.
 *   - It never caches a rejection. A transient failure that stuck for the whole
 *     TTL would be strictly worse than not caching at all.
 */

/** Long enough to cover a detail-view round trip, short enough that a list
 *  someone edited in another tab is not stale for long. Mutations invalidate
 *  explicitly (see `invalidateRequest`), so this only bounds passive staleness. */
export const DEFAULT_CACHE_TTL_MS = 60_000;

interface CacheEntry {
  at: number;
  promise: Promise<unknown>;
}

const entries = new Map<string, CacheEntry>();
let currentScope: string | null = null;

/**
 * Cached data is scoped to the signed-in tenant AND user. `logout()` navigates
 * client-side without reloading the page, so module state outlives a session
 * change: without this, the next person to sign in on the same tab would be
 * served the previous session's team directory and tag vocabulary. Re-reading
 * the scope on every lookup means that can never be forgotten at a call site.
 */
function readScope(): string {
  if (typeof window === 'undefined') return '';
  try {
    const tenant = JSON.parse(localStorage.getItem('admin_tenant') || 'null');
    const user = JSON.parse(localStorage.getItem('admin_user') || 'null');
    return `${tenant?._id ?? tenant?.id ?? ''}:${user?._id ?? user?.id ?? ''}`;
  } catch {
    // Unparseable auth state is treated as its own scope rather than as "same
    // as before" — failing closed here costs a refetch, failing open leaks.
    return '?';
  }
}

/**
 * Returns the cached promise for `key` when it is still fresh, otherwise runs
 * `load` and caches that.
 */
export function cachedRequest<T>(
  key: string,
  load: () => Promise<T>,
  ttlMs: number = DEFAULT_CACHE_TTL_MS
): Promise<T> {
  const scope = readScope();
  if (scope !== currentScope) {
    entries.clear();
    currentScope = scope;
  }

  const hit = entries.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return hit.promise as Promise<T>;

  const entry: CacheEntry = { at: Date.now(), promise: null as never };
  entry.promise = load().catch((error) => {
    // Only evict if this entry is still the live one — a refresh that already
    // replaced it must not be undone by the old request failing late.
    if (entries.get(key) === entry) entries.delete(key);
    throw error;
  });
  entries.set(key, entry);
  return entry.promise as Promise<T>;
}

/** Drops one key — call after a mutation that changes what it returns. */
export function invalidateRequest(key: string): void {
  entries.delete(key);
}

/** Drops everything. Exported for tests and for an explicit session teardown. */
export function clearRequestCache(): void {
  entries.clear();
  currentScope = null;
}
